<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div class="text-center">
        <h2 class="text-3xl font-bold text-gray-900">Logga in</h2>
        <p class="mt-2 text-sm text-gray-600">
          Har du inget konto?
          <router-link :to="registerLink" class="font-medium text-indigo-600 hover:text-indigo-500">
            Registrera dig här
          </router-link>
        </p>
      </div>

      <form @submit.prevent="login" class="mt-8 space-y-6">
        <!-- Email Field -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
            E-postadress *
          </label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            :class="[
              'appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm',
              emailError ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="din@email.se"
            @blur="validateEmail"
          />
          <p v-if="emailError" class="mt-1 text-sm text-red-600">{{ emailError }}</p>
        </div>

        <!-- Password Field -->
        <div>
          <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
            Lösenord *
          </label>
          <input
            id="password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            required
            :class="[
              'appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm',
              passwordError ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Ditt lösenord"
            @blur="validatePassword"
          />
          <div class="flex items-center justify-end mt-1">
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="text-sm text-gray-500 hover:text-gray-700"
            >
              {{ showPassword ? 'Dölj' : 'Visa' }} lösenord
            </button>
          </div>
          <p v-if="passwordError" class="mt-1 text-sm text-red-600">{{ passwordError }}</p>
        </div>

        <!-- Submit Button -->
        <div>
          <button
            type="submit"
            :disabled="loading || !isFormValid"
            :class="[
              'group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
              loading || !isFormValid
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700'
            ]"
          >
            <span v-if="loading" class="absolute left-0 inset-y-0 flex items-center pl-3">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            </span>
            {{ loading ? 'Loggar in...' : 'Logga in' }}
          </button>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="rounded-md bg-red-50 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <span class="text-red-400">⚠️</span>
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-700">{{ errorMessage }}</p>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';

const router = useRouter();

function resolveRole() {
  const q = router.currentRoute.value.query.role;
  if (q === 'organizer' || q === 'buyer') return q;
  const saved = sessionStorage.getItem('selectedRole');
  if (saved === 'organizer' || saved === 'buyer') return saved;
  return 'buyer';
}

const role = ref(resolveRole());

// Persist role for navigation cycles
sessionStorage.setItem('selectedRole', role.value);

const registerLink = computed(() => ({ path: '/register', query: { role: role.value } }));
const store = useStore();

// Form data
const form = ref({
  email: '',
  password: ''
});

// UI state
const loading = ref(false);
const showPassword = ref(false);
const errorMessage = ref('');

// Validation errors
const emailError = ref('');
const passwordError = ref('');

// Validation functions
const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!form.value.email.trim()) {
    emailError.value = 'E-postadress är obligatorisk';
    return false;
  }
  if (!emailRegex.test(form.value.email)) {
    emailError.value = 'Ange en giltig e-postadress';
    return false;
  }
  emailError.value = '';
  return true;
};

const validatePassword = () => {
  if (!form.value.password) {
    passwordError.value = 'Lösenord är obligatoriskt';
    return false;
  }
  passwordError.value = '';
  return true;
};

// Form validation
const isFormValid = computed(() => {
  return form.value.email && 
         form.value.password &&
         !emailError.value &&
         !passwordError.value;
});

const login = async () => {
  // Validate all fields
  const isEmailValid = validateEmail();
  const isPasswordValid = validatePassword();

  if (!isEmailValid || !isPasswordValid) {
    return;
  }

  try {
    loading.value = true;
    errorMessage.value = '';

    console.log('Attempting login to:', '/api/auth/login');
    console.log('Environment:', process.env.NODE_ENV);
    console.log('Origin:', window.location.origin);
    
    const res = await axios.post('/api/auth/login', {
      email: form.value.email.trim().toLowerCase(),
      password: form.value.password
    }, {
      withCredentials: true
    });

    console.log('Login response:', res.data);

    const token = res.data.token;
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // Save user and token in Vuex
    store.commit('setUser', res.data.user);
    store.commit('setToken', token);

    const u = res.data.user || {};
    const dest = (u.role === 'organizer' || u.role === 'admin' || u.isOrganizer) ? '/events' : '/tickets';
    router.push(dest);
  } catch (error) {
    console.error('Login failed:',
      'status=', error.response?.status,
      'data=', error.response?.data,
      'message=', error.message,
      'config=', error.config
    );
    errorMessage.value = error.response?.data?.message || 'Fel vid inloggning.';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.input {
  display: block;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 0.375rem;
}
</style>
